import { useTranslation } from "react-i18next";

import type { JackpotCampaign } from "@/campaigns/modules/jackpot/types";
import { winsInLastDay } from "@/campaigns/modules/jackpot/types";
import { LatestWinsCard } from "@/components/campaigns/latest-wins-card";

/**
 * The "Latest wins" card of the jackpot sidebar: the shared feed card with jackpot dressing. The
 * rows are assembled upstream (`recent_wins` on the campaign, masked names when the backend
 * serves them), so all that is jackpot policy here is the pace line — "3 wins in the last 24
 * hours", dropped once the campaign has finished or when nothing fell inside the window. Nothing
 * renders before the first win: the shared card owns that rule.
 */
export const JackpotLatestWinsCard = ({
  jackpotCampaign,
  density,
  className,
}: {
  jackpotCampaign: JackpotCampaign;
  /** See `LatestWinsCard`. */
  density?: "default" | "dock";
  className?: string;
}) => {
  const { t } = useTranslation();
  const finished = jackpotCampaign.campaign.status === "FI";

  const renderPace = (now: number) => {
    if (finished) return null;
    const pace = winsInLastDay(jackpotCampaign, now);
    if (pace.count === 0) return null;
    return (
      <p className="flex items-center gap-2 font-heading text-[12px] leading-4 font-semibold text-foreground lg:text-[13px]">
        <span aria-hidden className="size-1.5 rounded-full bg-active" />
        {pace.plus
          ? t(
              "jackpot.winsLast24hPlus",
              "{{count}}+ wins in the last 24 hours",
              {
                count: pace.count,
              }
            )
          : t("jackpot.winsLast24h", "{{count}} wins in the last 24 hours", {
              count: pace.count,
            })}
      </p>
    );
  };

  return (
    <LatestWinsCard
      testId="jackpot-latest-wins"
      title={t("sprintMission.latestWins", "Latest wins")}
      rows={jackpotCampaign.recent_wins}
      renderPace={renderPace}
      finished={finished}
      density={density}
      className={className}
    />
  );
};
